/**
 * Step to log into an existing account for Clever Library users.
 */
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {type LoginUserData} from "@khan/auth-shared";
import {useLocation} from "@khan/urls";

import {useApolloGqlClient} from "~/data-access/gql/use-apollo-gql-client.ts";
// eslint-disable-next-line static-service/require-shared-package-imports-only -- TODO: Port this over to the auth package.
import LinkAccountLoginForm from "~/districts-package/components/link-account-login-form.tsx";

import {
    completePostLogin,
    linkClever,
    loginCleverUser,
} from "./login-actions.ts";

interface Props {
    defaultIdentifier?: string;
    isTeacher: boolean;
    transferToken?: string;
    onForgotPasswordClick: () => void;
    onStudentLogin: (isDistrictUser: boolean, user: LoginUserData) => void;
    onLoginSubmit: () => void;
    onLoginError: () => void;
    titleId?: string;
}

export default function LoginStep({
    defaultIdentifier,
    isTeacher,
    transferToken,
    onForgotPasswordClick,
    onStudentLogin,
    onLoginSubmit,
    onLoginError,
    titleId,
}: Props) {
    const {gqlClient} = useApolloGqlClient();
    const location = useLocation();

    const submitLogin = React.useCallback(
        (identifier: string, password: string) => {
            onLoginSubmit();

            // Error handling is done by the LinkAccountLoginForm
            return loginCleverUser(gqlClient, identifier, password).then(
                (loginData) => {
                    if (isTeacher) {
                        if (transferToken) {
                            return linkClever(gqlClient, {transferToken}).then(
                                () => {
                                    completePostLogin(
                                        gqlClient,
                                        isTeacher,
                                        "/",
                                        loginData,
                                        location,
                                    );
                                },
                            );
                        }
                        completePostLogin(
                            gqlClient,
                            isTeacher,
                            "/",
                            loginData,
                            location,
                        );
                    } else {
                        const isDistrictUser =
                            !!loginData.user.cleverId ||
                            !!loginData.user.userDistrictInfos?.length;
                        onStudentLogin(isDistrictUser, loginData.user);
                    }
                },
            );
        },
        [
            gqlClient,
            isTeacher,
            transferToken,
            location,
            onStudentLogin,
            onLoginSubmit,
        ],
    );

    return (
        <View>
            <Strut size={spacing.medium_16} />
            <LinkAccountLoginForm
                titleId={titleId}
                isTeacher={isTeacher}
                isCleverLibrary={true}
                cleverToken={transferToken}
                defaultIdentifier={defaultIdentifier}
                continueUrl="/"
                onForgotPasswordClick={onForgotPasswordClick}
                handleLogin={submitLogin}
                onLoginError={onLoginError}
            />
        </View>
    );
}
